import React, { useState } from 'react';
import { Reorder, useDragControls, motion, useMotionValue, animate } from 'framer-motion';
import LNImage from '@/components/complex/ln-image';
import { PhotoPickerFile } from '@/components/common/photo-picker';
import Typography from '@/components/primitives/typography';
import Container from '@/components/primitives/container';
import { GripVertical } from 'lucide-react';

import styles from './styles.module.scss';

interface PhotoListProps {
  photos: PhotoPickerFile[];
  onReorder: (photos: PhotoPickerFile[]) => void;
  onDelete: (index: number) => void;
  setIsDragging: (isDragging: boolean) => void;
}

interface PhotoItemProps {
  photo: PhotoPickerFile;
  index: number;
  onDelete: (index: number) => void;
  setIsDragging: (isDragging: boolean) => void;
}

const PhotoItem: React.FC<PhotoItemProps> = ({ photo, index, onDelete, setIsDragging }) => {
  const [isReordering, setIsReordering] = useState(false);
  const dragControls = useDragControls();
  const x = useMotionValue(0);
  const swipeThreshold = 100;

  const handleDragEnd = (event: any, info: any) => {
    const { offset } = info;

    if (Math.abs(offset.x) > swipeThreshold) {
      // 스와이프 거리가 임계값을 넘으면 삭제 애니메이션 후 삭제
      animate(x, offset.x > 0 ? 300 : -300, {
        duration: 0.2,
        ease: "easeOut"
      }).then(() => {
        onDelete(index);
      });
    } else {
      // 스와이프 거리가 부족하면 원래 위치로 복귀
      animate(x, 0, {
        type: "spring",
        stiffness: 300,
        damping: 30
      });
    }
  };

  return (
    <Reorder.Item
      key={photo.id}
      value={photo}
      className={styles.reorderItem}
      whileDrag={{ scale: 1.02, zIndex: 10, touchAction: 'none' }}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.1}
      dragListener={false}
      dragControls={dragControls}
      onDragStart={() => {
        x.set(0);
        setIsReordering(true);
        setIsDragging(true);
      }}
      onDragEnd={() => {
        setIsReordering(false);
        setIsDragging(false);
      }}
    >
      <motion.div
        style={{ x }}
        className={styles.motionWrapper}
      >
        <Container
          rootClassName={styles.rootPhotoItem}
          className={styles.photoItem}
          aurora={isReordering}
        >
          {/* Main content area - handles horizontal swipe for deletion */}
          <motion.div
            className={styles.contentArea}
            drag={isReordering ? false : 'x'}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            dragMomentum={false}
            style={{ x, touchAction: 'pan-y' }}
          >
            <div className={styles.imageContainer}>
              <LNImage
                source={photo.file.url}
                width={64}
                height={64}
                variant={photo.isUploaded ? 'original' : ''}
                alt={`Photo ${index + 1}`}
                className={styles.photoImage}
              />
            </div>
          </motion.div>

          {/* Right handle area - only for drag reordering, no scroll interference */}
          <div className={styles.handleContainer}>
            <div
              className={styles.dragHandle}
              onPointerDown={(event) => {
                // Start drag reordering when touching the handle
                event.stopPropagation();
                dragControls.start(event);
              }}
              style={{ touchAction: 'none' }}
            >
              <GripVertical width={24} height={24} color="#666" />
            </div>
          </div>
        </Container>
      </motion.div>
    </Reorder.Item>
  );
};

/**
 * Component for displaying and reordering photos
 */
const PhotoList: React.FC<PhotoListProps> = ({ photos, onReorder, onDelete, setIsDragging }) => {
  return (
    <div className={styles.container}>
      <Typography className={styles.title}>사진 순서 조정</Typography>
      <Typography className={styles.description}>
        우측 핸들을 드래그해서 순서를 바꾸거나 좌우로 스와이프해서 삭제할 수 있어요
      </Typography>
      <Reorder.Group
        axis="y"
        values={photos}
        onReorder={onReorder}
        className={styles.reorderGroup}
      >
        {photos.map((photo, index) => (
          <PhotoItem
            key={photo.id}
            photo={photo}
            index={index}
            onDelete={onDelete}
            setIsDragging={setIsDragging}
          />
        ))}
      </Reorder.Group>
    </div>
  );
};

export default PhotoList;